/**
 * RebookNoticeCard — the frame around one {@link RebookNotice} (BON-146). The
 * words live in `rebookNoticeCopy`.
 *
 * A blocking notice gets the solid border and the "Blocked" badge; a
 * proceed-with-consequences one gets a dashed border and a padlock. The
 * difference is load-bearing rather than decorative: the CSR reading it needs to
 * know in one glance whether anything they do in this drawer can help.
 */
import { AlertTriangle, Lock } from 'lucide-react';

import { noticeBody, NOTICE_TITLE } from './rebookNoticeCopy';
import type { RebookNotice } from './rebookNotices';

export interface RebookNoticeCardProps {
  notice: RebookNotice;
  /** Rendered inside the body — the override checkbox, for the one notice that
   *  has one, so the consent sits with the consequence. */
  children?: React.ReactNode;
}

export function RebookNoticeCard({ notice, children }: RebookNoticeCardProps) {
  const hard = notice.tone === 'block';
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 ${
        hard
          ? 'border-amber-500 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/15'
          : 'border-dashed border-amber-500 bg-amber-50/60 dark:border-amber-700 dark:bg-amber-900/10'
      }`}
    >
      <span className="mt-0.5 flex-shrink-0 text-amber-700 dark:text-amber-400">
        {hard ? <AlertTriangle size={17} /> : <Lock size={17} />}
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-bold text-bark-900 dark:text-stone-100">
            {NOTICE_TITLE[notice.detail.kind]}
          </span>
          {notice.blocking && (
            <span className="rounded-full border border-amber-600 px-1.5 text-[9px] font-extrabold uppercase leading-relaxed tracking-wider text-amber-700 dark:text-amber-400">
              Blocked
            </span>
          )}
        </div>
        <div className="mt-1 text-xs leading-relaxed text-stone-700 dark:text-stone-300">
          {noticeBody(notice.detail)}
          {children}
        </div>
      </div>
    </div>
  );
}
